import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useAccountProgressAPI from "../../../hooks/useGetAccountProgressAPI";
import PageTitle from "../../layouts/PageTitle";
import Pagination from "./components/Pagination";
import Stepper from "./components/Stepper";
import Technologies from "./components/Technologies";
import searchTable, {
  findPayeed,
  numberOfDays,
  sortedPrizes,
  sortTable,
  totalPrize,
} from "./utils";
import { categories } from "../../../services/api/models";

const QuestLists = () => {
  const [quests, setQuests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [fullData, setFullData] = useState([]);

  // Initialize activeTab from localStorage
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("lastActiveTab") || "hiring";
  });

  // Initialize filter with proper isHiring value based on activeTab
  const [filter, setFilter] = useState(() => {
    const initialTab = localStorage.getItem("lastActiveTab") || "hiring";
    return {
      "$sort[id]": -1,
      isHiring: initialTab === "hiring",
    };
  });

  const [sortedBy, setSortedBy] = useState("");
  const [sortStatus, setSortStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") || ""
  );
  const { progress } = useAccountProgressAPI();
  const [searchBut, setSearchBut] = useState(false);

  // Filter states
  const [selectedHiringProgress, setSelectedHiringProgress] = useState("");
  const [selectedTechnologies, setSelectedTechnologies] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  //categories
  const [queryCategories, setQueryCategories] = useState([]);

  useEffect(() => {
    handleGetQueryCategories();
    // eslint-disable-next-line
  }, []);

  // Dropdown states
  const [hiringProgressDropdownOpen, setHiringProgressDropdownOpen] =
    useState(false);
  const [technologiesDropdownOpen, setTechnologiesDropdownOpen] =
    useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [isTabChanging, setIsTabChanging] = useState(false);

  // Refs for click outside handling
  const hiringProgressDropdownRef = useRef(null);
  const technologiesDropdownRef = useRef(null);
  const categoriesDropdownRef = useRef(null);

  const technologies = [
    // Frontend
    "React js",
    "Angular",
    "Vue",
    "JavaScript",
    "TypeScript",
    "HTML",
    "CSS",
    "Flutter",
    "Dart",

    // Backend
    "nodeJS",
    "feathers.js",
    "NestJS",
    "ASP.NET",
    ".NET",
    "Flask",
    "Django",
    "Laravel",
    "Java",
    "Python",

    // Databases
    "MySQL",
    "PostgreSQL",
    "MSSQL",

    // AI / ML
    "AI",
    "Agents",
    "FlowiseAI",
    "TensorFlow",
    "LLM",
    "NLP",

    // Design & UX
    "Figma",
    "XD",
    "AdobePhotoshop",
    "Illustrator",
    "UX",
    "Miro",
    "Drawio",

    // Tools & Other
    "n8n",
    "Postman",
    "QA",
    "Office",
    "SIP",
  ];

  const phases = ["registration", "submission", "review", "referred", "ended"];

  // Get quest categories for quest filtering
  const handleGetQueryCategories = async () => {
    try {
      const response = await categories.categoriesGet();
      const data = await response.data.data;
      // Only keep name and id
      const filtered = Array.isArray(data)
        ? data.map(({ name, id }) => ({ name, id }))
        : [];
      setQueryCategories(filtered);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleTabChange = (tab) => {
    // Immediately set tab changing state to show loading
    setIsTabChanging(true);
    setActiveTab(tab);
    localStorage.setItem("lastActiveTab", tab);

    // Clear current data immediately to prevent flash
    setQuests([]);
    setFullData([]);

    // Reset all filters and update tab filter
    handleResetAllFilters();
    setFilter((prev) => ({
      ...prev,
      isHiring: tab === "hiring",
    }));

    // Reset pagination
    setSkip(0);
  };

  const isResetDisabled = useMemo(() => {
    const hasHiringProgress =
      selectedHiringProgress &&
      selectedHiringProgress !== "" &&
      selectedHiringProgress !==
        `${activeTab === "hiring" ? "All Progress" : "All Statuses"}`;

    const hasCategories = selectedCategories.length > 0;
    const hasTechnologies = selectedTechnologies.length > 0;

    return !(hasHiringProgress || hasCategories || hasTechnologies);
  }, [
    selectedHiringProgress,
    selectedCategories,
    selectedTechnologies,
    activeTab,
  ]);

  // Remove the useEffect that was causing the issue - we handle it in handleTabChange now

  // reset button
  const handleResetAllFilters = () => {
    const currentTab = activeTab; // Preserve current tab
    setFilter({
      "$sort[id]": -1,
      isHiring: currentTab === "hiring", // Keep the current tab's filter
    });
    setSearchValue("");
    setSelectedHiringProgress("");
    setSelectedCategories([]);
    setSelectedTechnologies([]);
    // Close any open dropdowns
    setHiringProgressDropdownOpen(false);
    setCategoriesDropdownOpen(false);
    setTechnologiesDropdownOpen(false);
    setSearchBut(false);
  };

  useEffect(() => {
    if (searchValue && !isLoading) {
      const result = searchTable(searchValue, fullData);
      setQuests(result);
      setSortedBy("");
    } else if (!isLoading) {
      setQuests(fullData);
    }
  }, [searchValue, isLoading, fullData]);

  // Click outside handler for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hiringProgressDropdownRef.current &&
        !hiringProgressDropdownRef.current.contains(event.target)
      ) {
        setHiringProgressDropdownOpen(false);
      }
      if (
        technologiesDropdownRef.current &&
        !technologiesDropdownRef.current.contains(event.target)
      ) {
        setTechnologiesDropdownOpen(false);
      }
      if (
        categoriesDropdownRef.current &&
        !categoriesDropdownRef.current.contains(event.target)
      ) {
        setCategoriesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSortTable = (column, status = "") => {
    let newSortStatus =
      status || sortedBy !== column || sortStatus === ""
        ? "asc"
        : sortStatus === "asc"
        ? "desc"
        : "";
    setSortStatus(newSortStatus);
    setSortedBy(column);
    if (newSortStatus) {
      setQuests(sortTable(quests, newSortStatus, column));
    } else {
      setSortStatus("");
      setSortedBy("");
      setQuests(fullData);
    }
  };

  const handleSearchInput = (value) => {
    setSearchValue(value);
    if (value.trim() === "") {
      searchParams.delete("search");
    } else {
      searchParams.set("search", value);
    }
    setSearchParams(searchParams);
  };

  const handleTechnologyToggle = (tech) => {
    setSelectedTechnologies((prev) =>
      prev.includes(tech) ? prev.filter((t) => t !== tech) : [...prev, tech]
    );
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const removeTechnology = (tech) => {
    setSelectedTechnologies((prev) => prev.filter((t) => t !== tech));
  };

  const removeCategory = (category) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  //set query
  useEffect(() => {
    // Start with base filter including current tab's isHiring status
    const baseFilter = {
      "$sort[id]": -1,
      isHiring: activeTab === "hiring",
    };

    // Add additional filters
    if (selectedTechnologies.length > 0) {
      console.log("Selected Technologies:", selectedTechnologies);
      baseFilter["technologies[$in][]"] = selectedTechnologies;
    }

    if (selectedCategories.length > 0) {
      baseFilter["categoryId[$in][]"] = selectedCategories;
      console.log("Selected Categories:", selectedCategories);
    }

    if (selectedHiringProgress) {
      baseFilter["phase"] = selectedHiringProgress;
      console.log("Selected Hiring Progress:", selectedHiringProgress);
    }

    setFilter(baseFilter);
  }, [
    selectedHiringProgress,
    selectedTechnologies,
    selectedCategories,
    activeTab,
  ]);

  return (
    <>
      {/* <PageTitle activeMenu="Quest List" motherMenu="App" /> */}
      {progress < 100 && (
        <div
          className="p-3 rounded-3 mb-4"
          style={{
            background:
              progress < 50 ? "#FFEDD5" : progress < 80 ? "#FEF3C7" : "#E0F2FE",
            border:
              "1px solid " +
              (progress < 50
                ? "#F93A0B"
                : progress < 80
                ? "#FBBF24"
                : "#38BDF8"),
          }}
        >
          <div className="d-flex align-items-start justify-content-between gap-3 uncomplete-profile">
            <div className="d-flex align-items-start gap-3 sec1">
              {progress < 50 ? (
                <i
                  className="bi bi-exclamation-triangle-fill text-orange"
                  style={{ fontSize: "2rem" }}
                ></i>
              ) : progress < 80 ? (
                <i
                  className="bi bi-hourglass-split"
                  style={{ fontSize: "2rem", color: "#FBBF24" }}
                ></i>
              ) : (
                <i
                  className="bi bi-flag-fill"
                  style={{ fontSize: "2rem", color: "#38BDF8" }}
                ></i>
              )}

              <div>
                <h5 className="text-black mb-1">Complete your profile</h5>
                {progress < 50 ? (
                  <span className="text-black">
                    Lets get started - Your profile is{" "}
                    <span className="fw-bold">{progress}% complete Only!!</span>{" "}
                    - finish it now your chances in Hiring Quests
                  </span>
                ) : progress < 80 ? (
                  <span className="text-black">
                    Halfway through - Keep going! Your profile is{" "}
                    <span className="fw-bold">{progress}% complete</span> -
                    finish it now your chances in Hiring Quests
                  </span>
                ) : (
                  <span className="text-black">
                    You're almost there! Your profile is{" "}
                    <span className="fw-bold">{progress}% complete</span> -
                    finish it now your chances in Hiring Quests
                  </span>
                )}
              </div>
            </div>
            <Link
              to="/app-profile"
              className="btn"
              style={{
                background:
                  progress < 50
                    ? "#F93A0B"
                    : progress < 80
                    ? "#FBBF24"
                    : "#38BDF8",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                height: "auto",
                display: "flex",
                alignItems: "center",
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                whiteSpace: "nowrap",
              }}
            >
              Complete Now
            </Link>
          </div>
        </div>
      )}

      <div className="mt-4 mb-4 d-flex flex-direction-start">
        <div
          className="hiring-selection p-1 rounded-3 d-flex gap-3"
          style={{
            background: "#FFFFFF",
            display: "inline-flex",
            border: "1px solid #E5E7EB",
            maxWidth: "25.6rem",
          }}
        >
          <div
            className="d-flex align-items-center gap-2 type-hiring"
            style={{
              background: activeTab === "hiring" ? "#FFEDD5" : "transparent",
              cursor: "pointer",
              borderRadius: "5px",
              padding: "0.525rem 1rem",
              fontWeight: "600",
            }}
            onClick={() => handleTabChange("hiring")}
          >
            <i
              className="bi bi-suitcase-lg"
              style={{
                fontSize: "1rem",
                color: activeTab === "hiring" ? "#F93A0B" : "black",
              }}
            ></i>
            <span
              style={{
                fontSize: ".825rem",
                color: activeTab === "hiring" ? "#F93A0B" : "black",
              }}
            >
              Hiring-Based Quests
            </span>
          </div>
          <div
            className="d-flex align-items-center gap-2 type-hiring"
            style={{
              background: activeTab === "task" ? "#FFEDD5" : "transparent",
              cursor: "pointer",
              borderRadius: "5px",
              padding: "0.325rem 1rem",
              fontWeight: "600",
            }}
            onClick={() => handleTabChange("task")}
          >
            <i
              className="bi bi-trophy"
              style={{
                fontSize: "1rem",
                color: activeTab === "task" ? "#F93A0B" : "black",
              }}
            ></i>
            <span
              style={{
                fontSize: ".825rem",
                color: activeTab === "task" ? "#F93A0B" : "black",
              }}
            >
              Task-Based Quests
            </span>
          </div>
        </div>
      </div>

      <div
        className="d-flex flex-sm-row align-items-sm-center align-items-start justify-content-between mb-4 flex-wrap gap-3"
        style={{ background: "#FFFFFF", borderRadius: "8px" }}
      >
        <div
          className="w-100"
          style={{ borderBottom: "1px solid #E5E7EB", padding: "2rem" }}
        >
          <div
            className="input-group search-area"
            style={{
              maxWidth: "500px",
            }}
          >
            <span
              className="input-group-text"
              onClick={() => {
                setSearchBut(!searchBut);
              }}
              style={{
                border: "1px solid rgb(216 216 216)",
                borderRadius: "8px 0 0 8px",
                borderRight: "none",
                background: "rgb(249 249 249)",
              }}
            >
              <i className="flaticon-381-search-2"></i>
            </span>
            <input
              type="text"
              value={searchValue}
              onChange={(event) => handleSearchInput(event.target.value)}
              className={`form-control ${searchBut ? "active" : ""}`}
              placeholder="Search Quests by name"
              style={{
                border: "1px solid rgb(216 216 216)",
                borderRadius: "0 8px 8px 0",
                padding: "0.5rem 0.75rem",
                borderLeft: "none",
                background: "rgb(249 249 249)",
                paddingLeft: "0",
              }}
            />
          </div>
        </div>

        {/* Updated Filters section with Reset button */}
        <div
          className="d-flex align-items-center justify-content-between w-100"
          style={{
            padding: "0.5rem 2rem 0",
          }}
        >
          <span
            style={{
              color: "#000",
              fontSize: "1rem",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i
              className="bi bi-funnel"
              style={{ fontSize: "1.2rem", color: "#f93a0b" }}
            ></i>
            Filters
          </span>

          {/* Reset Button */}
          <button
            onClick={handleResetAllFilters}
            disabled={isResetDisabled}
            className={`btn btn-sm ${
              isResetDisabled ? "btn-outline-light" : "btn-outline-secondary"
            }`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.875rem",
              padding: "0.375rem 0.75rem",
              border: `1px solid ${isResetDisabled ? "#e9ecef" : "#dee2e6"}`,
              borderRadius: "6px",
              background: isResetDisabled ? "#f8f9fa" : "transparent",
              color: isResetDisabled ? "#adb5bd" : "#6c757d",
              transition: "all 0.2s ease",
              marginRight: "0.5rem",
              cursor: isResetDisabled ? "not-allowed" : "pointer",
              opacity: isResetDisabled ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isResetDisabled) {
                e.target.style.background = "#f8f9fa";
                e.target.style.borderColor = "#adb5bd";
              }
            }}
            onMouseLeave={(e) => {
              if (!isResetDisabled) {
                e.target.style.background = "transparent";
                e.target.style.borderColor = "#dee2e6";
              }
            }}
          >
            <i
              className="bi bi-arrow-counterclockwise"
              style={{
                fontSize: "0.875rem",
                opacity: isResetDisabled ? 0.5 : 1,
              }}
            ></i>
            Reset All
          </button>
        </div>

        <div className="d-flex flex-wrap w-100 filter-dropdowns text-black">
          <div
            className="row g-4"
            style={{
              width: "100%",
              margin: "0",
              padding: "1.5rem",
              paddingTop: "0",
            }}
          >
            {/* Hiring Progress Filter */}
            <div className="col-md-4" style={{ marginTop: "0" }}>
              <div className="form-group mb-2">
                <label
                  htmlFor="HiringProgress"
                  className="form-label font-w600"
                >
                  {activeTab === "hiring" ? "Hiring Progress" : "Statuses"}
                </label>
                <div
                  ref={hiringProgressDropdownRef}
                  className={`my-select form-control solid mb-1 ${
                    hiringProgressDropdownOpen ? "dropdown-open" : ""
                  }`}
                  onClick={() =>
                    setHiringProgressDropdownOpen(!hiringProgressDropdownOpen)
                  }
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="dropdown-selected text-black">
                    {selectedHiringProgress.charAt(0).toUpperCase() +
                      selectedHiringProgress.slice(1) ||
                      `${
                        activeTab === "hiring" ? "All Progress" : "All Statuses"
                      }`}
                  </div>

                  {hiringProgressDropdownOpen && (
                    <ul
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: "0",
                        right: "0",
                        zIndex: 1000,
                        backgroundColor: "#fff",
                        border: "1px solid rgb(216 216 216)",
                        borderRadius: "0.75rem",
                        maxHeight: "180px",
                        overflowY: "auto",
                        listStyle: "none",
                        padding: "0",
                        margin: "0",
                      }}
                    >
                      {phases
                        .filter((phase) => {
                          if (activeTab === "hiring") {
                            return phase !== "ended";
                          }
                          if (activeTab === "task") {
                            return phase !== "referred";
                          }
                          return true; // Show all phases for other tabs
                        })
                        .map((phase) => (
                          <li
                            key={phase}
                            className="dropdown-option"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedHiringProgress(phase);
                              setHiringProgressDropdownOpen(false);
                            }}
                            style={{
                              padding: "0.5rem 0.75rem",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              color: "#888888",
                              textTransform: "capitalize",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#F93A0B";
                              e.target.style.color = "#fff";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#888888";
                            }}
                          >
                            {phase}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Categories Filter - Selected Items Below */}
            <div className="col-md-4" style={{ marginTop: "0" }}>
              <div className="form-group mb-2">
                <label htmlFor="categories" className="form-label font-w600">
                  Categories
                </label>

                <div
                  ref={categoriesDropdownRef}
                  className="my-select form-control d-flex align-items-center mb-1"
                  onClick={() =>
                    setCategoriesDropdownOpen(!categoriesDropdownOpen)
                  }
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    justifyContent: "space-between",
                  }}
                >
                  <span className="text-black">
                    {selectedCategories.length === 0
                      ? "All Categories"
                      : `${selectedCategories.length} categories selected`}
                  </span>

                  {categoriesDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: "0",
                        right: "0",
                        zIndex: 1000,
                        backgroundColor: "#fff",
                        border: "1px solid rgb(216 216 216)",
                        borderRadius: "0.75rem",
                        maxHeight: "200px",
                        overflowY: "auto",
                        padding: "1rem",
                      }}
                    >
                      {(queryCategories || []).map((category, index) => (
                        <div
                          key={category.id}
                          className="form-check"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            id={`category-${category.id}`}
                            className="form-check-input"
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                          />
                          <label
                            htmlFor={`category-${category.id}`}
                            className="form-check-label"
                            style={{ cursor: "pointer" }}
                          >
                            {category.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected items displayed BELOW the dropdown */}
                {selectedCategories.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {selectedCategories.map((category, index) => (
                      <span
                        key={index}
                        className="d-flex align-items-center"
                        style={{
                          padding: "0.25rem 0.7rem",
                          fontSize: "0.75rem",
                          borderRadius: "15px",
                          fontWeight: "500",
                          background: "#F93A0B",
                          color: "#FFFFFF",
                        }}
                      >
                        {
                          queryCategories.find((cat) => cat.id === category)
                            ?.name
                        }
                        <i
                          className="bi bi-x-circle ms-2"
                          style={{
                            cursor: "pointer",
                            fontSize: "0.925rem",
                          }}
                          onClick={() => removeCategory(category)}
                        ></i>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Technologies Filter */}
            <div className="col-md-4" style={{ marginTop: "0" }}>
              <div className="form-group mb-2">
                <label htmlFor="technologies" className="form-label font-w600">
                  Technologies
                </label>

                {/* Dropdown field - now at the top */}
                <div
                  ref={technologiesDropdownRef}
                  className="my-select form-control d-flex align-items-center mb-2"
                  onClick={() =>
                    setTechnologiesDropdownOpen(!technologiesDropdownOpen)
                  }
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    justifyContent: "space-between",
                  }}
                >
                  <span className="text-black">
                    {selectedTechnologies.length === 0
                      ? "All Technologies"
                      : `${selectedTechnologies.length} selected`}
                  </span>

                  {technologiesDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: "0",
                        right: "0",
                        zIndex: 1000,
                        backgroundColor: "#fff",
                        border: "1px solid rgb(216 216 216)",
                        borderRadius: "0.75rem",
                        maxHeight: "200px",
                        overflowY: "auto",
                        padding: "1rem",
                      }}
                    >
                      {technologies.map((tech, index) => (
                        <div
                          key={index}
                          className="form-check"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            id={`tech-${index}`}
                            className="form-check-input"
                            checked={selectedTechnologies.includes(tech)}
                            onChange={() => handleTechnologyToggle(tech)}
                          />
                          <label
                            htmlFor={`tech-${index}`}
                            className="form-check-label"
                            style={{ cursor: "pointer" }}
                          >
                            {tech}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected badges - now below the dropdown */}
                {selectedTechnologies.length > 0 && (
                  <div className="d-flex flex-wrap gap-1">
                    {selectedTechnologies.map((tech, index) => (
                      <span
                        key={index}
                        className="d-flex align-items-center"
                        style={{
                          padding: "0.25rem 0.7rem",
                          fontSize: "0.75rem",
                          borderRadius: "15px",
                          fontWeight: "500",
                          background: "#F93A0B",
                          color: "#FFFFFF",
                        }}
                      >
                        {tech}
                        <i
                          className="bi bi-x-circle ms-2"
                          style={{
                            cursor: "pointer",
                            fontSize: "0.875rem",
                          }}
                          onClick={() => removeTechnology(tech)}
                        ></i>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: "5%" }}>
        <div className="col-xl-13">
          <div className="table-responsive">
            <table className="quest-table table display mb-4 dataTablesCard job-table table-responsive-xl card-table dataTable no-footer">
              <thead>
                <tr style={{ textAlign: "center" }}>
                  <th>Quest</th>
                  <th>Type</th>
                  {activeTab === "task" ? (
                    <>
                      <th>Prize</th>
                      <th>Progress</th>
                      <th>Days Left</th>
                      <th>Status</th>
                      <th>Award Status</th>
                      <th>Technologies</th>
                    </>
                  ) : (
                    <>
                      <th>Job Type</th>
                      <th>Salary Range</th>
                      <th>Experience Level</th>
                      <th>Company</th>
                      <th>Days Left</th>
                      <th>Hiring Status</th>
                      <th>Technologies</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody style={{ textAlign: "center" }}>
                {(isLoading || isTabChanging) && (
                  <tr>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <div className="spinner-border" role="status"></div>
                      </div>
                    </td>
                    <td></td>
                    <td></td>
                    {activeTab === "task" && <td></td>}
                  </tr>
                )}
                {!(isLoading || isTabChanging) && quests.map((quest, index) => (
                  <tr key={quest.id}>
                    {/* ...existing code for rendering quest rows... */}
                    <td style={{ fontSize: "1rem" }}>
                      <Link
                        to={`/quest-view/${quest.id}`}
                        className="text-decoration-underline text-secondary"
                      >
                        {quest.title}
                      </Link>
                    </td>
                    <td className="wspace-no" style={{ fontSize: "1rem" }}>
                      {quest.category.icon} {quest.category.name}
                    </td>
                    {activeTab === "task" ? (
                      <>
                        <td style={{ fontSize: "1rem" }}>
                          {quest.prizes !== null && quest.prizes.length > 0 ? (
                            <>
                              🏆 {quest.prizes[0].currency === "USD" && "$"}
                              {totalPrize(quest.prizes)}
                              {quest.prizes[0].currency === "EGP" && " EGP"}
                            </>
                          ) : (
                            <span className="text-muted">No Prize</span>
                          )}
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          <Stepper phase={quest.phase} />
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          {numberOfDays(quest)}
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          <span
                            className={`badge badge-lg light badge-${quest.phase}`}
                          >
                            {quest.phase?.charAt(0).toUpperCase() +
                              quest.phase.slice(1)}
                          </span>
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          <div className="d-flex gap-1 justify-content-center">
                            {quest.prizes && findPayeed(quest.prizes) ? (
                              <>
                                {sortedPrizes(quest.prizes).map(
                                  (prize, index) =>
                                    prize.payee && prize.payee.id ? (
                                      <div
                                        key={index}
                                        className="position-relative"
                                      >
                                        <div className="winner-circle">
                                          <p className="m-0 fs-24">
                                            {+prize.rank === 1
                                              ? "🥇"
                                              : +prize.rank === 2
                                              ? "🥈"
                                              : "🥉"}
                                          </p>
                                        </div>
                                        <span className="bg-dark text-white winner-name">
                                          {prize.payee.nickname}
                                        </span>
                                      </div>
                                    ) : null
                                )}
                              </>
                            ) : (
                              <span
                                className={`badge badge-lg light badge-warning`}
                              >
                                unawarded
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Technologies tech={quest.technologies} />
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ color: "#F93A0B", fontSize: "1rem" }}>{`${
                          quest.hiringType
                        } ${quest.workingStatus || "N/A"}`}</td>
                        <td
                          style={{
                            color: "#0e7f0e",
                            fontWeight: "500",
                            fontSize: "1rem",
                          }}
                        >
                          {quest.minSalary !== null ||
                          quest.maxSalary !== null ? (
                            quest.minSalary !== 0 || quest.maxSalary !== 0 ? (
                              <>
                                {quest.currency?.toLowerCase() === "usd" && "$"}
                                {quest.minSalary && quest.maxSalary
                                  ? `${quest.minSalary} - ${quest.maxSalary}`
                                  : quest.minSalary
                                  ? `${quest.minSalary}`
                                  : `Up to ${quest.maxSalary}`}
                                {quest.currency?.toLowerCase() === "egp" &&
                                  " EGP"}
                                {quest.currency?.toLowerCase() === "gbp" &&
                                  " GBP"}
                              </>
                            ) : (
                              "Experience Based"
                            )
                          ) : (
                            "Not Determined"
                          )}
                        </td>
                        <td className="text-black" style={{ fontSize: "1rem" }}>
                          {quest.minExperience && quest.maxExperience
                            ? `${quest.minExperience}-${quest.maxExperience} years`
                            : quest.minExperience
                            ? `${quest.minExperience}+ years`
                            : quest.maxExperience
                            ? `Up to ${quest.maxExperience} years`
                            : "N/A"}
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          <Link
                            to={quest.org?.website}
                            target="_blank"
                            className="text-decoration-underline text-secondary"
                          >
                            {quest.org?.name || "N/A"}
                          </Link>
                        </td>
                        <td style={{ fontSize: "1rem" }}>
                          {numberOfDays(quest)}
                        </td>
                        <td
                          style={{
                            position: "relative",
                            overflow: "visible",
                            fontSize: "1rem",
                          }}
                        >
                          <span
                            className={`badge badge-lg light badge-${quest.phase}`}
                            style={{
                              position: "relative",
                              overflow: "visible",
                            }}
                          >
                            {quest.phase?.charAt(0).toUpperCase() +
                              quest.phase.slice(1)}{" "}
                            {(quest.phase === "referred" ||
                              quest.phase === "review") && (
                              <span className="tooltip-container">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-exclamation-circle"
                                  viewBox="0 0 16 16"
                                  style={{ cursor: "pointer" }}
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                                  <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                                </svg>

                                <div className="tooltip-text">
                                  {quest.phase === "referred"
                                    ? "Top candidates have been selected and sent to the hiring company."
                                    : "Code Quests is currently reviewing all submissions to select the top candidates. stay tuned!"}
                                  {/* Triangle arrow */}
                                  <div className="tooltip-arrow"></div>
                                </div>

                                {/* Invisible bridge to keep tooltip visible */}
                                <div className="tooltip-bridge"></div>
                              </span>
                            )}
                          </span>
                        </td>
                        <td>
                          <Technologies tech={quest.technologies} />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          setQuests={setQuests}
          setIsLoading={setIsLoading}
          setSkip={setSkip}
          skip={skip}
          setFullData={setFullData}
          sortedBy={sortedBy}
          sortStatus={sortStatus}
          setSortStatus={setSortStatus}
          setSortedBy={setSortedBy}
          filter={filter}
          setIsTabChanging={setIsTabChanging} // Add this line
        />
      </div>
    </>
  );
};

export default QuestLists;
